/**
 * Borradores de formularios guardados en el navegador (localStorage).
 * Sobreviven a cerrar el modal, cambiar de página o recargar.
 *
 * Uso típico en un formulario de alta:
 *   const draft = useFormDraft("cliente", form, () => setForm(emptyForm()));
 *   // al abrir "Nuevo":  setForm(draft.load() ?? emptyForm());
 *   // en el Dialog:      <Dialog values={form} draft={draft} …>
 *   // al guardar OK:     draft.clear();
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";

const PREFIX = "borrador:";

function read(key) {
  try { return JSON.parse(localStorage.getItem(PREFIX + key)); } catch { return null; }
}
function write(key, value) {
  try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch { /* storage no disponible */ }
}
function remove(key) {
  try { localStorage.removeItem(PREFIX + key); } catch { /* storage no disponible */ }
}

/**
 * @param key      identificador del formulario ("presupuesto", "cliente", …)
 * @param value    datos actuales del formulario (lo que se guarda al elegir "Guardar borrador")
 * @param onReset  vacía el formulario (lo usa "Empezar de cero")
 */
export function useFormDraft(key, value, onReset) {
  const [restored, setRestored] = useState(false);
  // El formulario abierto está ligado al borrador guardado (se abrió con load())
  const [active, setActive] = useState(false);
  const detach = useCallback(() => {
    setActive(false);
    setRestored(false);
  }, []);

  return {
    restored,
    /** Devuelve el borrador guardado (o null) y liga el formulario a él. */
    load() {
      const d = read(key);
      setActive(true);
      setRestored(!!d);
      return d;
    },
    /** El formulario se abrió por otro camino: no tocar el borrador guardado al descartar. */
    detach,
    save() {
      write(key, value);
      setActive(true);
      toast.success("Borrador guardado. Se recupera al volver a abrir el formulario.");
    },
    /** Borra el borrador guardado (al descartar o después de guardar con éxito). */
    clear() {
      if (active) remove(key);
      setRestored(false);
    },
    /** "Empezar de cero": borra el borrador y vacía el formulario. */
    reset() {
      remove(key);
      setRestored(false);
      onReset?.();
    },
  };
}
